import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { alpha, Box, IconButton, useTheme } from "@mui/material";
import BurgerIcon from "@icons/myicons/sidebar/burger.svg?react";
import CloseIcon from "@icons/myicons/sidebar/close.svg?react";
import { Profile } from "../Profile";

const Container = styled(motion.div)<{
  collapsed?: boolean;
}>`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  z-index: 4000;
  background-color: ${({ theme }) => theme.palette.background.sidebar};
  align-items: stretch;
  overflow: hidden;
  padding: ${({ theme, collapsed }) =>
    collapsed ? theme.spacing(0, 4) : theme.spacing(0, 4, 6, 4)};
  "&::-webkit-scrollbar": {
    display: none;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  height: 54px;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 0)};
`;

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  overflow-y: auto;
  gap: ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.breakpoints.down("sm")} {
    justify-content: flex-start;
  }
`;

export interface SidebarResponsiveProps {
  header: ReactNode;
  children: ReactNode;
  collapsed: boolean;
  onMenuOpen: () => void;
}

export const SidebarResponsive = ({
  header,
  children,
  collapsed,
  onMenuOpen,
}: SidebarResponsiveProps) => {
  const theme = useTheme();
  // added extra 400 px to max height in case of overflow. Can't use too large value because animation does not look good
  const maxSidebarHeight = window.innerHeight;

  return (
    <Container
      collapsed={collapsed}
      animate={{ height: collapsed ? 54 : maxSidebarHeight }}
    >
      <Box
        width="100%"
        height="100%"
        display="flex"
        gap={6}
        flexDirection="column"
        justifyContent="space-between"
        flex="1"
        // overflow but hide scrollbar
        overflow="auto"
        sx={{
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box display="flex" flexDirection="column" flex="1" gap={2}>
          <HeaderContainer>
            {header}
            <IconButton
              onClick={onMenuOpen}
              sx={{
                color: theme.appSidebar?.openButton?.color,
                "&:hover": {
                  backgroundColor:
                    theme.appSidebar?.openButton?.hoverBackground ??
                    `${alpha(theme.palette.button.textButton.hover, 0.1)} !important`,
                },
                "&:active": {
                  backgroundColor:
                    theme.appSidebar?.openButton?.activeBackground ??
                    `${alpha(theme.palette.button.textButton.hover, 0.2)} !important`,
                },
              }}
            >
              {collapsed ? <BurgerIcon /> : <CloseIcon />}
            </IconButton>
          </HeaderContainer>
          <BodyContainer>{children}</BodyContainer>
        </Box>
        <Profile />
      </Box>
    </Container>
  );
};
